/**
 * SCViewOnlyBanner — one-line amber banner shown on detail pages when a
 * Sales Coordinator is looking at a record they didn't create / aren't
 * assigned to. Gives immediate explanation for why Edit / Delete / etc.
 * are disabled so SCs don't waste clicks hunting for a working button.
 *
 * Usage:
 *   <SCViewOnlyBanner record={lead} />
 *
 * Renders nothing when:
 *   - the caller isn't a Sales Coordinator, or
 *   - the SC owns the record (full edit rights).
 */

import React from 'react';
import { Eye, Info } from '@phosphor-icons/react';
import useSCPermissions from '../hooks/useSCPermissions';

export default function SCViewOnlyBanner({ record, className = '' }) {
  const { isSC, canEditRecord } = useSCPermissions();
  if (!isSC) return null;
  if (!record) return null;         // No record yet (still loading) → stay quiet
  if (canEditRecord(record)) return null;

  return (
    <div
      data-testid="sc-view-only-banner"
      className={`flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 ${className}`}
    >
      <Eye size={18} weight="fill" className="mt-0.5 flex-shrink-0 text-amber-600" />
      <div className="flex-1 leading-snug">
        <strong className="font-semibold">View-only mode.</strong>{' '}
        You can see this record but cannot edit, delete, reassign, or share it. Contact the President to request access.
      </div>
    </div>
  );
}
